// src/pages/RecursosHumanos/Colaboradores/index.tsx
import React, { useMemo } from "react";
import Table from "../../../../../components/TableV2";
import TitleButtons from "../../../../../components/TitleButtons";
import { useEmpresasCliente } from "./colaboradoresData"; // mantém o nome por compatibilidade
import { useNavigate } from "react-router-dom";

// =============== Tipos ===============
type Colaborador = {
  id?: string | number;
  nome_completo: string;
  cpf?: string | null;
  setor?: string | null; // exibido como "Departamento"
  data_de_nascimento?: string | null;
  status_atual?: string | null;
};

// =============== Utils ===============
/** Aceita { data: [...] } ou array direto, e normaliza chaves */
function normalizeColaboradores(raw: any): Colaborador[] {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : [];
  return (arr as any[]).map((x) => ({
    id: x.id ?? x.colaborador_id ?? x.user_id ?? undefined,
    nome_completo:
      x.nome_completo ?? x.nomeCompleto ?? x.nome ?? x.full_name ?? "",
    cpf: x.cpf ?? x.CPF ?? x.documento ?? null,
    setor: x.setor ?? x.departamento ?? x.area ?? null,
    data_de_nascimento:
      x.data_de_nascimento ?? x.nascimento ?? x.birth_date ?? null,
    status_atual:
      x.status_atual ?? x.statusAtual ?? x.status ?? x.situacao ?? null,
  }));
}

function normalizeStatusText(raw: string | null | undefined): string {
  if (!raw) return "";
  let s = String(raw).trim().toLowerCase();
  s = s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, " ");
  return s;
}

// =============== Página ===============
const Colaboradores: React.FC = () => {
  // Colunas: ID oculto, Nome, Departamento, Status
  const headers = useMemo(
    () => ["", "Nome Completo", "Departamento", "Status"],
    []
  );

  const columnWidths = useMemo(() => ["0px", "420px", "260px", "160px"], []);

  const hook = useEmpresasCliente?.();
  const data = normalizeColaboradores((hook as any)?.data ?? hook);

  const rows = useMemo<React.ReactNode[][]>(() => {
    return data.map((c) => [
      String(c.id ?? ""), // coluna oculta
      c.nome_completo || "-",
      c.setor || "-",
      c.status_atual || "-",
    ]);
  }, [data]);

  const navigate = useNavigate();
  const idIdx = 0;

  const getStatusCellStyle = (
    _rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    _originalIndex: number,
    _rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ): React.CSSProperties | undefined => {
    if (headerTitle !== "Status") return undefined;
    if (colIndex !== 3) return undefined;

    if (typeof value !== "string" && typeof value !== "number") {
      return undefined;
    }

    const txt = normalizeStatusText(String(value));

    const greenPastel = "#DCFCE7"; // disponível
    const redPastel = "#FEE2E2"; // atestado/licença/férias
    const orangePastel = "#FEF3C7"; // em serviço

    let bg: string | null = null;

    if (txt.startsWith("disponivel")) {
      bg = greenPastel;
    } else if (txt.includes("atestado medico")) {
      bg = redPastel;
    } else if (txt.includes("licenca maternidade")) {
      bg = redPastel;
    } else if (txt === "ferias" || txt.startsWith("ferias ")) {
      bg = redPastel;
    } else if (txt === "em servico" || txt.startsWith("em servico")) {
      bg = orangePastel;
    }

    if (!bg) return undefined;

    return {
      backgroundColor: bg,
      color: "#111",
      fontWeight: 500,
      textTransform: "uppercase",
    };
  };

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title="Coordenação - Colaboradores"
        buttons={
          [
            /* {
            label: "Registrar Colaborador",
            onClick: () =>
              navigate("/recursos-humanos/colaboradores/registrar-colaborador"),
            variant: "primary",
          },*/
          ]
        }
      />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        responsiveWidths={{
          1300: { 0: "0px", 1: "360px", 2: "220px", 3: "120px" },
          1000: { 0: "0px", 1: "320px", 2: "0px", 3: "110px" },
        }}
        onDoubleClick={(_viewIndex, rowData) => {
          const id = String(rowData[idIdx] ?? "");
          if (id) {
            navigate(`/recursos-humanos/colaborador/${encodeURIComponent(id)}`);
          }
        }}
        enableCellColors={true}
        getCellStyle={getStatusCellStyle}
      />
    </div>
  );
};

export default Colaboradores;
